// UpgradePrompt — shown when a Free user hits a Pro-gated surface.
// Single component used by Partner day view, Nutrients, AI flows, etc.
// During beta, isPro() returns true for everyone so this rarely renders
// in practice — but it's wired so the moment billing flips on, every
// gated surface degrades gracefully.

import { Sparkles } from "lucide-react";

interface Props {
  /** Short reason why this surface needs Pro (1 sentence). */
  reason: string;
  /** Optional feature-list bullets shown below the headline. */
  features?: string[];
  /** Optional override for the CTA label (defaults to "Start 7-day free trial"). */
  ctaLabel?: string;
  /**
   * Action when the user taps the CTA. During beta, we don't have a
   * checkout flow yet — default is a no-op that we'll wire to the IAP
   * sheet when billing lands.
   */
  onUpgrade?: () => void;
}

export default function UpgradePrompt({
  reason,
  features,
  ctaLabel = "Start 7-day free trial",
  onUpgrade
}: Props) {
  return (
    <div className="mx-auto max-w-md p-6 rounded-2xl bg-gradient-to-br from-emerald-950/50 via-slate-900 to-slate-950 border border-emerald-900/50 text-center space-y-4">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/40">
        <Sparkles className="w-6 h-6 text-emerald-300" />
      </div>
      <div>
        <h3 className="text-base font-semibold text-slate-100">Unlock with Pro</h3>
        <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">{reason}</p>
      </div>
      {features && features.length > 0 && (
        <ul className="text-left text-xs text-slate-400 space-y-1.5 max-w-xs mx-auto">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <span className="text-emerald-400 flex-shrink-0 mt-0.5">✓</span>
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={onUpgrade ?? (() => alert("Billing isn't wired up yet — Pro is on for all beta users."))}
        className="w-full max-w-xs mx-auto py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold text-sm"
      >
        {ctaLabel}
      </button>
      <p className="text-[11px] text-slate-500">
        $40/yr · cancel anytime · no card needed for trial
      </p>
    </div>
  );
}
